'use client';

import { ARIA_LABELS } from '@/constants/labels';
import { getScorePercentage, getSkillLevel } from '@/domains';
import { selectDisplayScoreValue } from '@/domains/scoring';
import { useCallback, useMemo } from 'react';
import { IUseScorecardFeatureParams, IUseScorecardFeatureReturn, TScoreDisplaySize } from './types';

/**
 * Encapsulates all non-visual Scorecard business logic.
 *
 * Why this hook exists:
 * - Keeps rendering concerns inside the component and behavioral/derived concerns in one place.
 * - Makes scorecard rules easier to test without depending on DOM rendering.
 * - Centralizes computed values shared by different scorecard variants.
 *
 * What this hook computes:
 * - `scoreSize`: Maps component size to the score display size variant.
 * - `skillLevel`: Uses explicit level when provided, otherwise derives from score.
 * - `handleShareWithManager`: Safely invokes share callback only when valid.
 * - `ariaScoreLabel`: Generates a consistent accessible score phrase.
 * - `isStatusVariant` and `isSimpleVariant`: Variant flags used by UI branching.
 */
export const useScorecardFeature = ({
  level,
  maxScore,
  onShareWithManager,
  runtimeRatingCutoffs,
  score,
  scoreCardId,
  scorePercentage,
  size,
  variant,
}: IUseScorecardFeatureParams): IUseScorecardFeatureReturn => {
  // ================================
  // Derived size mapping
  // ================================
  // The score display style supports `compact | standard | large`, while the
  // component prop supports `compact | standard | expanded`.
  // We normalize `expanded` to `large` so style lookups remain type-safe.
  const scoreSize: TScoreDisplaySize = size === 'expanded' ? 'large' : size;

  // ================================
  // Score value selection
  // ================================
  // For percentage format, we want to display the percentage score instead of the raw score.
  // This ensures that the displayed value is consistent with the skill level.
  // For other formats, we display the raw score as is.
  const scoreValue = useMemo(() => {
    return selectDisplayScoreValue({
      fallback: 0,
      score,
      scorePercentage: getScorePercentage({ score, scoreMax: maxScore, scorePercentage }),
    });
  }, [score, scorePercentage, maxScore]);

  // ================================
  // Skill-level resolution
  // ================================
  // Rule precedence:
  // 1) If a `level` prop is supplied, trust it as the source of truth.
  // 2) Otherwise derive a level from score via `getSkillLevel`.
  //
  // Note: the normalization formula is intentionally preserved exactly as
  // originally implemented to avoid changing runtime behavior.
  const skillLevel = useMemo(() => {
    if (level) return level;
    return getSkillLevel(scoreValue, runtimeRatingCutoffs);
  }, [level, runtimeRatingCutoffs, scoreValue]);

  // ================================
  // Share action handler
  // ================================
  // Ensures share callback fires only when both:
  // - a scoreCardId exists, and
  // - a callback was provided by the consumer.
  //
  // The callback receives a string id because downstream APIs expect a string.
  const handleShareWithManager = useCallback(() => {
    if (scoreCardId && onShareWithManager) {
      onShareWithManager(scoreCardId.toString());
    }
  }, [onShareWithManager, scoreCardId]);

  // ================================
  // Accessibility label construction
  // ================================
  // Builds a user-facing aria label from the centralized template,
  // replacing placeholders with current score/max values.
  // Falls back to `0` when score is missing.
  const ariaScoreLabel = useMemo(() => {
    return ARIA_LABELS.SCORE_OUT_OF.replace(
      '%SCORE_VALUE%',
      score ? score.toString() : '0',
    ).replace('%MAX_SCORE%', maxScore.toString());
  }, [maxScore, score]);

  // ================================
  // Variant flags
  // ================================
  // Small booleans that simplify branching logic in the presentational
  // component and keep conditions readable.
  const isStatusVariant = variant === 'violation' || variant === 'pass' || variant === 'fail';
  const isSimpleVariant = variant === 'simple';

  // ================================
  // Feature contract return
  // ================================
  return {
    ariaScoreLabel,
    handleShareWithManager,
    isSimpleVariant,
    isStatusVariant,
    scoreSize,
    scoreValue,
    skillLevel,
  };
};

export default useScorecardFeature;
